import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useDispatch } from 'react-redux';
import { logoutUser } from "../../../../../redux/WithcookieSlice";
import { useTranslation } from "../../../../../redux/TranslationHelper";

const MobileNavMenu = ({ menus = [], isLoggedIn = false }) => {
  const [expandedItems, setExpandedItems] = useState([]);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();


  // 메뉴 정렬 및 필터링 (NavMenu.js 참조)
  const sortedMenus = Array.isArray(menus) 
    ? [...menus].sort((a, b) => (a.order || 0) - (b.order || 0)) 
    : [];
  
  // parentId가 null인 메인 메뉴 항목만 필터링 (글로벌 메뉴 제외)
  // 로그인 상태에 따라 메뉴 필터링
  const mainMenus = sortedMenus.filter(
    menu => menu && menu.parentId === null && 
    (menu.visible !== false) && 
    menu.type !== 'GLOBAL' &&
    (
      (!menu.is_logged && !menu.is_not_logged) || // 로그인 관련 속성이 없는 경우 모두 표시
      (menu.is_logged && isLoggedIn) ||           // 로그인 필요 메뉴는 로그인 시만 표시
      (menu.is_not_logged && !isLoggedIn)         // 비로그인 메뉴는 로그아웃 시만 표시
    )
  );
  

  // 모바일 메뉴 닫기 함수
  const closeMobileMenu = () => {
    const offcanvasMobileMenu = document.querySelector(
      "#offcanvas-mobile-menu"
    );
    if (offcanvasMobileMenu) {
      offcanvasMobileMenu.classList.remove("active");
      // body 클래스도 제거
      document.body.classList.remove("menu-open");
      // 햄버거 메뉴 버튼에서도 active 클래스 제거
      document.querySelectorAll('.hamburger-menu').forEach(hamburger => {
        hamburger.classList.remove('active');
      });
    }
  };

  // 메뉴 클릭 핸들러
  const handleMenuClick = (e, menu) => {
    e.preventDefault();
    
    // 메뉴 액션 처리
    if (menu.menuAction === 'logout') {
      dispatch(logoutUser()).then(() => {
        // 로그아웃 성공 후 홈 페이지로 이동
        navigate('/');
        // 메뉴 닫기
        closeMobileMenu();
      });
      return;
    }
    
    // 외부 링크 처리
    if (menu.externalLink) {
      window.open(menu.externalLink, '_blank');
      // 메뉴 닫기
      closeMobileMenu();
      return;
    }
    
    // 일반 내부 링크 처리
    navigate(menu.path || '/');
    // 메뉴 닫기
    closeMobileMenu();
  };

  // 스타일 설정
  const styles = {
    menuItem: {
      padding: '14px 0',
      borderBottom: '1px solid #f0f0f0',
      position: 'relative',
      margin: 0,
      width: '100%',
      backgroundColor: '#fff',
      transition: 'background-color 0.2s'
    },
    subMenuItem: {
      padding: '12px 0',
      borderBottom: '1px solid #f0f0f0',
      margin: 0,
      width: '100%',
      position: 'relative',
      backgroundColor: '#fff',
      transition: 'background-color 0.2s'
    },
    toggleIcon: {
      position: 'absolute',
      right: '20px',
      top: '50%',
      transform: 'translateY(-50%)',
      cursor: 'pointer',
      width: '50px',
      height: '50px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: '50%',
      transition: 'all 0.2s ease',
      color: '#666'
    },
    menuWrapper: {
      position: 'relative',
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      backgroundColor: 'transparent',
      padding: '0 25px 0 25px'
    },
    link: {
      color: '#333',
      textDecoration: 'none',
      display: 'block',
      fontSize: '15px',
      fontWeight: '500',
      wordWrap: 'break-word',
      width: 'calc(100% - 30px)', // 토글 버튼 공간 확보
      whiteSpace: 'normal',
      transition: 'color 0.2s',
      position: 'relative',
      paddingLeft: 0
    },
    submenu: {
      display: 'none',
      width: '100%',
      paddingLeft: 0,
      margin: 0,
      backgroundColor: '#fff',
      transition: 'all 0.3s ease'
    },
    expandedSubmenu: {
      display: 'block',
      width: '100%',
      paddingLeft: 0,
      margin: 0,
      backgroundColor: '#fff',
      transition: 'all 0.3s ease'
    }
  };

  // 서브메뉴 토글 함수
  const toggleSubmenu = (menuId, e) => {
    // 버블링 방지
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    
    // 현재 스크롤 위치 기억
    const scrollPosition = window.scrollY;
    
    setExpandedItems(prevItems => {
      if (prevItems.includes(menuId)) {
        return prevItems.filter(id => id !== menuId);
      } else {
        return [...prevItems, menuId];
      }
    });
    
    // 토글 후 스크롤 위치 복원 (setTimeout으로 상태 업데이트 후 실행)
    setTimeout(() => {
      window.scrollTo({
        top: scrollPosition,
        behavior: 'auto' // 부드러운 스크롤 대신 즉시 이동
      });
    }, 10);
  };

  // 더 모던한 아이콘 구현
  const ChevronDown = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9"></polyline>
    </svg>
  );
  
  const ChevronUp = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="18 15 12 9 6 15"></polyline>
    </svg>
  );

  // 내부 링크 클릭 핸들러
  const handleLinkClick = (path) => {
    navigate(path);
    // 메뉴 닫기
    closeMobileMenu();
  };

  // 하위 메뉴 렌더링 함수
  const renderSubmenus = (parentId, level = 1) => {
    const subMenus = sortedMenus.filter(
      menu => menu && menu.parentId === parentId && 
      (menu.visible !== false) &&
      (
        (!menu.is_logged && !menu.is_not_logged) || // 로그인 관련 속성이 없는 경우 모두 표시
        (menu.is_logged && isLoggedIn) ||           // 로그인 필요 메뉴는 로그인 시만 표시
        (menu.is_not_logged && !isLoggedIn)         // 비로그인 메뉴는 로그아웃 시만 표시
      )
    ).sort((a, b) => (a.order || 0) - (b.order || 0));

    if (subMenus.length === 0) return null;

    return (
      <ul style={expandedItems.includes(parentId) ? styles.expandedSubmenu : styles.submenu}>
        {subMenus.map((subMenu) => {
          const hasChildren = sortedMenus.some(menu => menu && menu.parentId === subMenu.id);
          
          return (
            <li key={subMenu.id} style={{
              ...styles.subMenuItem, 
              paddingLeft: 0,
              backgroundColor: '#fff'
            }}>
              <div style={{
                ...styles.menuWrapper,
                paddingLeft: 25 + (level * 15)
              }}>
                {subMenu.externalLink || subMenu.menuAction ? (
                  // 외부 링크나 특수 액션이 있는 서브메뉴
                  <a 
                    href="#" 
                    style={{
                      ...styles.link,
                      fontSize: '14px',
                      color: '#555'
                    }} 
                    onClick={(e) => handleMenuClick(e, subMenu)}
                  >
                    {/* 기본값 처리 */}
                    {t(subMenu.name || '서브메뉴')}
                  </a>
                ) : (
                  // 일반 내부 링크 서브메뉴
                  <a 
                    href="#" 
                    style={{
                      ...styles.link,
                      fontSize: '14px',
                      color: '#555'
                    }} 
                    onClick={(e) => {
                      e.preventDefault();
                      handleLinkClick(subMenu.path);
                    }}
                  >
                    {t(subMenu.name)}
                  </a>
                )}
                
                {hasChildren && (
                  <div
                    style={styles.toggleIcon}
                    onClick={(e) => toggleSubmenu(subMenu.id, e)}
                  >
                    {expandedItems.includes(subMenu.id) ? <ChevronUp /> : <ChevronDown />}
                  </div>
                )}
              </div>
              
              {hasChildren && renderSubmenus(subMenu.id, level + 1)}
            </li>
          );
        })}
      </ul>
    );
  };
  
  // 메뉴가 없는 경우 기본 메뉴 표시
  if (!mainMenus || mainMenus.length === 0) {
    return (
      <nav className="offcanvas-navigation" id="offcanvas-navigation">
        <ul>
          <li className="menu-item">
            <Link to="/" onClick={closeMobileMenu}>{t('홈')}</Link>
          </li>
          <li className="menu-item">
            <Link to="/shopping" onClick={closeMobileMenu}>{t('쇼핑')}</Link>
          </li>
          <li className="menu-item">
            <Link to="/cart" onClick={closeMobileMenu}>{t('장바구니')}</Link>
          </li>
          <li className="menu-item">
            <Link to="/my-account" onClick={closeMobileMenu}>{t('내 계정')}</Link>
          </li>
          <li className="menu-item">
            <Link to="/contact" onClick={closeMobileMenu}>{t('고객센터')}</Link>
          </li>
        </ul>
      </nav>
    );
  }

  return (
    <nav 
      className="offcanvas-navigation" 
      style={{ 
        padding: 0, 
        maxWidth: '100%', 
        overflowX: 'hidden',
        overflowY: 'auto',
        flex: 1,
        backgroundColor: '#fff',
        height: '100%',
        width: '100%'
      }}
    >
      <ul style={{ 
        width: '100%', 
        padding: 0, 
        margin: 0,
        backgroundColor: '#fff',
        listStyle: 'none'
      }}>
        {mainMenus.map((menu) => {
          const hasChildren = sortedMenus.some(item => item && item.parentId === menu.id);
          
          return (
            <li key={menu.id} style={styles.menuItem}>
              <div style={styles.menuWrapper}>
                {menu.externalLink || menu.menuAction ? (
                  // 외부 링크나 특수 액션이 있는 메뉴
                  <a href="#" style={styles.link} onClick={(e) => handleMenuClick(e, menu)}>
                    {t(menu.name)}
                  </a>
                ) : (
                  // 일반 내부 링크 메뉴
                  <a href="#" style={styles.link} onClick={(e) => {
                    e.preventDefault();
                    handleLinkClick(menu.path);
                  }}>
                    {t(menu.name)}
                  </a>
                )}
                
                {hasChildren && (
                  <div
                    style={styles.toggleIcon}
                    onClick={(e) => toggleSubmenu(menu.id, e)}
                  >
                    {expandedItems.includes(menu.id) ? <ChevronUp /> : <ChevronDown />}
                  </div>
                )}
              </div>
              
              {hasChildren && renderSubmenus(menu.id)}
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default MobileNavMenu;
